import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import { formatCurrency, formatCurrencyLabel } from '../../utils/formatUtils';
import '../styles/admin-theme.css';

const AdminPricebookDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pricebook, setPricebook] = useState(null);
  const [entries, setEntries] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planLicenses, setPlanLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [showPricebookModal, setShowPricebookModal] = useState(false);
  const [pricebookForm, setPricebookForm] = useState({ name: '', currency: 'USD', is_active: true });

  const [showEntryModal, setShowEntryModal] = useState(false);
  const [entryModalError, setEntryModalError] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    name: '',
    object_name: '',
    productid: '',
    plan_id: '',
    list_price: '',
    discount_percent: '',
    tax_percent: '',
    is_active: true,
  });

  const loadPricebook = useCallback(async () => {
    if (!id || id === 'new') return;
    setLoading(true);
    setError('');
    try {
      const pb = await adminSchemaApi.fetchPricebookById(id);
      setPricebook(pb);
      if (pb) {
        setPricebookForm({
          name: pb.name || '',
          currency: pb.currency || 'USD',
          is_active: !!pb.is_active,
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to load pricebook');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadEntries = useCallback(async () => {
    if (!id || id === 'new') return;
    setEntriesLoading(true);
    try {
      const list = await adminSchemaApi.fetchPricebookEntries(id);
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || 'Failed to load entries');
    } finally {
      setEntriesLoading(false);
    }
  }, [id]);

  const loadLicenseTypes = useCallback(async () => {
    try {
      const list = await adminSchemaApi.fetchLicenseTypes();
      setLicenseTypes(Array.isArray(list) ? list : []);
    } catch {
      setLicenseTypes([]);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const list = await adminSchemaApi.fetchPlans();
      setPlans(Array.isArray(list) ? list : []);
    } catch {
      setPlans([]);
    }
  }, []);

  useEffect(() => {
    if (id === 'new') {
      navigate('/admin/pricebooks', { replace: true });
      return;
    }
    loadPricebook();
  }, [id, loadPricebook, navigate]);

  useEffect(() => {
    if (pricebook) loadEntries();
  }, [pricebook, loadEntries]);

  useEffect(() => {
    loadLicenseTypes();
  }, [loadLicenseTypes]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const loadPlanLicenses = useCallback(async (planId) => {
    if (!planId) {
      setPlanLicenses([]);
      return;
    }
    try {
      const list = await adminSchemaApi.fetchPlanLicenses(planId);
      setPlanLicenses(Array.isArray(list) ? list : []);
    } catch {
      setPlanLicenses([]);
    }
  }, []);

  useEffect(() => {
    loadPlanLicenses(entryForm.plan_id || null);
  }, [entryForm.plan_id, loadPlanLicenses]);

  const handleUpdatePricebook = async (e) => {
    e.preventDefault();
    if (!id || id === 'new') return;
    setSaving(true);
    setError('');
    try {
      const record = await adminSchemaApi.updatePricebook(id, pricebookForm);
      setPricebook(record);
      setShowPricebookModal(false);
    } catch (err) {
      setError(err?.message || 'Failed to update pricebook');
    } finally {
      setSaving(false);
    }
  };

  const openEntryModal = (entry = null) => {
    setEntryModalError('');
    setEditingEntry(entry);
    if (entry) {
      setEntryForm({
        name: entry.name || '',
        object_name: entry.object_name || '',
        productid: entry.productid || '',
        plan_id: entry.plan_id || '',
        list_price: entry.list_price != null ? entry.list_price : '',
        discount_percent: entry.discount_percent != null ? entry.discount_percent : '',
        tax_percent: entry.tax_percent != null ? entry.tax_percent : '',
        is_active: !!entry.is_active,
      });
    } else {
      setEntryForm({
        name: '',
        object_name: 'license_type',
        productid: '',
        plan_id: '',
        list_price: '',
        discount_percent: '',
        tax_percent: '',
        is_active: true,
      });
    }
    setShowEntryModal(true);
  };

  const availableLicenseTypes = entryForm.plan_id
    ? licenseTypes.filter((lt) =>
        planLicenses.some((pl) => pl.license_type_id === lt.id) ||
        (editingEntry && lt.id === entryForm.productid)
      )
    : [];

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!id || id === 'new') return;
    if (!entryForm.plan_id) {
      setEntryModalError('Plan is required when creating a pricebook entry.');
      return;
    }
    setSaving(true);
    setEntryModalError('');
    try {
      const body = {
        object_name: entryForm.object_name || undefined,
        productid: entryForm.productid || null,
        plan_id: entryForm.plan_id || null,
        list_price: entryForm.list_price === '' ? null : Number(entryForm.list_price),
        discount_percent: entryForm.discount_percent === '' ? null : Number(entryForm.discount_percent),
        tax_percent: entryForm.tax_percent === '' ? null : Number(entryForm.tax_percent),
        is_active: entryForm.is_active,
      };
      if (editingEntry) {
        await adminSchemaApi.updatePricebookEntry(id, editingEntry.id, body);
      } else {
        await adminSchemaApi.createPricebookEntry(id, body);
      }
      setShowEntryModal(false);
      loadEntries();
    } catch (err) {
      setEntryModalError(err?.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!id || id === 'new' || !window.confirm('Remove this entry?')) return;
    setSaving(true);
    setError('');
    try {
      await adminSchemaApi.deletePricebookEntry(id, entryId);
      loadEntries();
    } catch (err) {
      setError(err?.message || 'Failed to delete entry');
    } finally {
      setSaving(false);
    }
  };

  if (id === 'new') return null;

  if (loading || !pricebook) {
    return (
      <>
        <Link to="/admin/pricebooks" className="btn btn-outline-secondary btn-sm mb-3">← Back to Pricebooks</Link>
        <p className="admin-subtitle">{loading ? 'Loading…' : 'Pricebook not found.'}</p>
      </>
    );
  }

  return (
    <>
      <Link to="/admin/pricebooks" className="btn btn-outline-secondary btn-sm mb-3">
        ← Back to Pricebooks
      </Link>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="admin-title mb-1">{pricebook.name || 'Pricebook'}</h2>
          <p className="admin-subtitle small mb-0">
            Currency: {formatCurrencyLabel(pricebook.currency)} · {pricebook.is_active ? 'Active' : 'Inactive'}
          </p>
        </div>
        <Button variant="outline-primary" size="sm" onClick={() => setShowPricebookModal(true)}>
          <FaEdit className="me-1" /> Edit pricebook
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Entries (prices per license type)</h5>
        <Button className="admin-primary-btn btn-sm" onClick={() => openEntryModal(null)}>
          + Add entry
        </Button>
      </div>
      <p className="admin-subtitle small mb-2">
        Add entries per plan + license type. Same plan can have entries in different pricebooks. Select plan first, then license type.
      </p>

      {entriesLoading ? (
        <p className="admin-subtitle">Loading entries…</p>
      ) : (
        <Table striped bordered hover size="sm" responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>License type</th>
              <th>Plan</th>
              <th>List price</th>
              <th>Discount %</th>
              <th>Tax %</th>
              <th>Active</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.name || '—'}</td>
                <td>{entry.license_type_name || entry.license_type_code || (entry.productid ? '—' : '—')}</td>
                <td>{entry.plan_name || entry.plan_code || '—'}</td>
                <td>{entry.list_price != null ? formatCurrency(entry.list_price, pricebook?.currency) : '-'}</td>
                <td>{entry.discount_percent != null ? Number(entry.discount_percent) : '-'}</td>
                <td>{entry.tax_percent != null ? Number(entry.tax_percent) : '-'}</td>
                <td>{entry.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEntryModal(entry)}>
                    <FaEdit />
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeleteEntry(entry.id)} disabled={saving}>
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted">
                  No entries yet. Add entries per plan + license type above.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showPricebookModal} onHide={() => setShowPricebookModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Pricebook</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdatePricebook}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={pricebookForm.name}
                onChange={(e) => setPricebookForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Currency</Form.Label>
              <Form.Select
                value={pricebookForm.currency}
                onChange={(e) => setPricebookForm((p) => ({ ...p, currency: e.target.value }))}
              >
                <option value="USD">USD ($)</option>
                <option value="INR">INR (₹)</option>
                <option value="AED">AED (د.إ)</option>
              </Form.Select>
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="Active"
              checked={pricebookForm.is_active}
              onChange={(e) => setPricebookForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowPricebookModal(false)}>Cancel</Button>
            <Button type="submit" className="admin-primary-btn" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showEntryModal} onHide={() => { setShowEntryModal(false); setEntryModalError(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingEntry ? 'Edit Entry' : 'Add Entry'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveEntry}>
          <Modal.Body>
            {entryModalError && (
              <Alert variant="danger" dismissible onClose={() => setEntryModalError('')} className="mb-3">
                {entryModalError}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Plan</Form.Label>
              <Form.Select
                value={entryForm.plan_id}
                onChange={(e) => setEntryForm((p) => ({ ...p, plan_id: e.target.value, productid: '' }))}
                required
              >
                <option value="">— Select plan first —</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name || plan.code} ({plan.code})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">Plan must be selected first. License types will be filtered by plan.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>License type</Form.Label>
              <Form.Select
                value={entryForm.productid}
                onChange={(e) => setEntryForm((p) => ({ ...p, productid: e.target.value }))}
                required
                disabled={!entryForm.plan_id}
              >
                <option value="">— {entryForm.plan_id ? 'Select license type' : 'Select plan first'} —</option>
                {availableLicenseTypes.map((lt) => (
                  <option key={lt.id} value={lt.id}>
                    {lt.name || lt.code} ({lt.code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>List price ({pricebook?.currency ? formatCurrencyLabel(pricebook.currency) : ''})</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={entryForm.list_price}
                onChange={(e) => setEntryForm((p) => ({ ...p, list_price: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Discount %</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={entryForm.discount_percent}
                onChange={(e) => setEntryForm((p) => ({ ...p, discount_percent: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tax %</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={entryForm.tax_percent}
                onChange={(e) => setEntryForm((p) => ({ ...p, tax_percent: e.target.value }))}
              />
            </Form.Group>
            {editingEntry && editingEntry.name && (
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" value={editingEntry.name} readOnly plaintext className="form-control-plaintext" />
              </Form.Group>
            )}
            <Form.Check
              type="checkbox"
              label="Active"
              checked={entryForm.is_active}
              onChange={(e) => setEntryForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowEntryModal(false)}>Cancel</Button>
            <Button type="submit" className="admin-primary-btn" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AdminPricebookDetail;
